import { useGetUserProfile, useShareContactMessage } from "@/tanstack-hooks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { CogIcon, UserRoundIcon } from "lucide-react";
import { ContactType } from "@/generated-api";
import { useToast } from "@/hooks/use-toast";
import telegramIcon from "@/assets/telegram.svg";
import instagramIcon from "@/assets/instagram.svg";
import whatsappIcon from "@/assets/whatsapp.svg";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";

export default function ContactShareButton({
  counterpartyId,
}: {
  counterpartyId: string;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const userProfileQuery = useGetUserProfile();
  const shareContactMutation = useShareContactMessage();
  const t = useTranslation();

  const handleShareContact = (type: ContactType, value: string) => {
    shareContactMutation.mutate(
      {
        contactMessage: {
          counterparty_id: counterpartyId,
          contact_type: type,
        },
        value,
      },
      {
        onError: (error) => {
          console.error("Error - could not share contact", error);
          toast({
            variant: "destructive",
            title: t.MESSAGES.ERRORS.SEND,
            description: t.SHARED.ERRORS.PERSISTENT,
            className: "dark",
          });
        },
      }
    );
  };

  const user = userProfileQuery.data;

  const telegram = user?.contact_info?.telegram;
  const instagram = user?.contact_info?.instagram;
  const whatsapp = user?.contact_info?.whatsapp;

  const contactsNull =
    user?.contact_info === null ||
    (telegram === null && instagram === null && whatsapp === null);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="focus:outline-none select-none">
        <div className="rounded-full dark h-8 w-8 p-0 shrink-0 hover:bg-zinc-800 flex items-center justify-center cursor-pointer">
          <UserRoundIcon className="h-5 w-5 text-zinc-400" strokeWidth={2.5} />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="dark rounded-2xl space-y-2 min-w-xs ml-4 mb-4"
      >
        {contactsNull ? (
          <DropdownMenuItem
            onClick={() =>
              navigate("/profile/edit", {
                state: { scrollToBottom: true },
              })
            }
            className="gap-4 py-4 rounded-xl hover:cursor-pointer"
          >
            <CogIcon className="w-5 h-5" />
            {t.MESSAGES.LABELS.CONFIGURE_CONTACTS}
          </DropdownMenuItem>
        ) : (
          <>
            {telegram && (
              <DropdownMenuItem
                onClick={() => {
                  handleShareContact(ContactType.TELEGRAM, telegram);
                }}
                className="gap-4 py-4 rounded-xl hover:cursor-pointer"
              >
                <img src={telegramIcon} className="w-5 h-5" />
                Telegram
              </DropdownMenuItem>
            )}
            {instagram && (
              <DropdownMenuItem
                onClick={() => {
                  handleShareContact(ContactType.INSTAGRAM, instagram);
                }}
                className="gap-4 py-4 rounded-xl hover:cursor-pointer"
              >
                <img src={instagramIcon} className="w-5 h-5" />
                Instagram
              </DropdownMenuItem>
            )}
            {whatsapp && (
              <DropdownMenuItem
                onClick={() => {
                  handleShareContact(ContactType.WHATSAPP, whatsapp);
                }}
                className="gap-4 py-4 rounded-xl hover:cursor-pointer"
              >
                <img src={whatsappIcon} className="w-5 h-5" />
                Whatsapp
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
